import React, { useMemo, useState } from "react";
import { CircularProgress, Popover, Tooltip } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PestControlRoundedIcon from "@mui/icons-material/PestControlRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import BackgroundLetterAvatarsLogo from "../AvatarLogo";
import SearchableDropdown from "../Modal/SearchableDropDown";
import WipLimitControl, { isOverWip } from "../DeliveryTracker/WipLimitControl";
import { getPriorityIcon } from "../../utilities/Icons";
import "./kanbanBoard.scss";

const DAY_MS = 24 * 60 * 60 * 1000;
const DONE_PREVIEW = 6;

const TYPE_ICONS = { epic: BoltRoundedIcon, story: BookmarkRoundedIcon, task: TaskAltRoundedIcon, bug: PestControlRoundedIcon };

/* queue → in flight → done: the role a column plays in the flow */
export const stageOf = (title, index) => {
  if (String(title || "").trim().toLowerCase() === "done") return "done";
  return index === 0 ? "queue" : "flow";
};

/* how long a card has gone without being touched — the Kanban signal that
   work is stuck. Measured from its last update. */
export const idleOf = (issue, now = Date.now()) => {
  const time = new Date(issue?.updatedAt || issue?.createdAt).getTime();
  if (!Number.isFinite(time)) return null;
  const days = Math.max(0, Math.floor((now - time) / DAY_MS));
  const level = days >= 5 ? "stale" : days >= 2 ? "idle" : "fresh";
  return { days, level, label: days === 0 ? "Today" : `${days}d` };
};

const STAGE_META = {
  queue: { icon: InboxRoundedIcon, hint: "Ready to pull" },
  flow: { icon: AutorenewRoundedIcon, hint: "In progress" },
  done: { icon: CheckCircleRoundedIcon, hint: "Finished" },
};

const KanbanCard = ({ issue, stage, onOpen, onDragStart, onDragEnd, onAssign, assigning }) => {
  const type = String(issue.issueType || "story").toLowerCase();
  const TypeIcon = TYPE_ICONS[type] || BookmarkRoundedIcon;
  const person = Array.isArray(issue.assignee) ? issue.assignee[0] : null;
  const personName = person?.employeeName || person?.employeeEmail || "";
  const idle = stage === "done" ? null : idleOf(issue);

  return (
    <article
      className={`kb-card kb-card--${TYPE_ICONS[type] ? type : "story"} ${stage === "done" ? "is-done" : ""} ${idle?.level === "stale" ? "is-stale" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      data-testid={`kb-card-${issue.name}`}
    >
      <div className="kb-card__top">
        <span className="kb-card__type" title={issue.issueType || "Story"}><TypeIcon /></span>
        <button type="button" className="kb-card__key" onClick={() => onOpen(issue)}>{issue.name}</button>
        <Tooltip title={`${issue.priority || "Medium"} priority`}>
          <span className="kb-card__priority">{getPriorityIcon(issue.priority)}</span>
        </Tooltip>
      </div>

      <button type="button" className="kb-card__title" onClick={() => onOpen(issue)}>
        {issue.summary}
      </button>

      <div className="kb-card__foot">
        {idle ? (
          <Tooltip title={idle.days === 0 ? "Updated today" : `No activity for ${idle.days} day${idle.days === 1 ? "" : "s"}`}>
            <span className={`kb-idle is-${idle.level}`}>
              <ScheduleRoundedIcon />
              {idle.label}
            </span>
          </Tooltip>
        ) : (
          <span className="kb-idle is-done"><CheckCircleRoundedIcon />Done</span>
        )}
        {Number(issue.storyPointEstimate) > 0 ? (
          <Tooltip title="Story points"><span className="kb-points">{issue.storyPointEstimate}</span></Tooltip>
        ) : null}
        <button
          type="button"
          className="kb-assignee"
          onClick={(e) => onAssign(e, issue)}
          aria-label={personName ? `Assigned to ${personName}, change` : "Assign"}
          disabled={assigning}
        >
          {assigning ? (
            <CircularProgress size={16} />
          ) : personName ? (
            <BackgroundLetterAvatarsLogo LogoTitle={personName} RequiredFontSize="10px !important" />
          ) : (
            <span className="kb-assignee__empty"><PersonAddAlt1RoundedIcon /></span>
          )}
        </button>
      </div>
    </article>
  );
};

const AddCard = ({ onCreate }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const submit = async () => {
    const summary = text.trim();
    if (!summary) return;
    setText("");
    setOpen(false);
    await onCreate(summary);
  };
  if (!open) {
    return (
      <button type="button" className="kb-add" onClick={() => setOpen(true)}>
        <AddRoundedIcon /> Add card
      </button>
    );
  }
  return (
    <div className="kb-composer">
      <textarea
        autoFocus
        rows={2}
        placeholder="What needs to be done? Enter to add"
        value={text}
        aria-label="New card"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
          if (e.key === "Escape") { setOpen(false); setText(""); }
        }}
      />
      <div className="kb-composer__actions">
        <button type="button" className="kb-btn" onClick={() => { setOpen(false); setText(""); }}>Cancel</button>
        <button type="button" className="kb-btn is-primary" onClick={submit} disabled={!text.trim()}>Add</button>
      </div>
    </div>
  );
};

/**
 * The Kanban format of the active board: a flow ribbon over columns that
 * play a role (queue → in progress → done), WIP meters on the columns where
 * work is in flight, and cards that show how long they have sat idle.
 *
 * Moving, opening, assigning and creating go through the page's handlers, so
 * the board behaves the same as the sprint board underneath.
 */
const KanbanBoard = ({
  columns = [],
  issues = [],
  counts = {},
  wipLimits = {},
  canEdit = false,
  dragRef,
  activeZone,
  getZoneProps = () => ({}),
  clearZone = () => {},
  onDropToColumn,
  onOpen,
  onCreate,
  onWipSave,
  contributors = [],
  assigneeUpdating,
  onAssigneeUpdate,
}) => {
  const [assignFor, setAssignFor] = useState(null);
  const [showAllDone, setShowAllDone] = useState(false);

  const byColumn = useMemo(() => {
    const map = new Map(columns.map((c) => [String(c.title).toLowerCase(), []]));
    issues.forEach((issue) => {
      const list = map.get(String(issue?.status || "").toLowerCase());
      if (list) list.push(issue);
    });
    return map;
  }, [columns, issues]);

  const countOf = (title) => Number(counts?.[title]) || byColumn.get(String(title).toLowerCase())?.length || 0;
  const total = columns.reduce((sum, c) => sum + countOf(c.title), 0);
  const dragging = dragRef?.current;

  return (
    <div className="kb">
      {/* the flow, end to end: how much sits at each stage right now */}
      <ol className="kb-ribbon" aria-label="Flow">
        {columns.map((column, index) => {
          const stage = stageOf(column.title, index);
          const count = countOf(column.title);
          const over = stage === "flow" && isOverWip(count, wipLimits[column._id]);
          return (
            <li key={column._id || column.title} className={`kb-ribbon__step is-${stage} ${over ? "is-over" : ""}`} style={{ flexGrow: Math.max(1, count) }}>
              <span className="kb-ribbon__name">{column.title}</span>
              <span className="kb-ribbon__count">{count}</span>
              {total ? <span className="kb-ribbon__share">{Math.round((count / total) * 100)}%</span> : null}
            </li>
          );
        })}
      </ol>

      <div className="kb-columns">
        {columns.map((column, index) => {
          const stage = stageOf(column.title, index);
          const StageIcon = STAGE_META[stage].icon;
          const cards = byColumn.get(String(column.title).toLowerCase()) || [];
          const count = countOf(column.title);
          const limit = stage === "flow" ? wipLimits[column._id] || null : null;
          const over = isOverWip(count, limit);
          const zone = `status-${column.title}`;
          const isTarget = dragging?.draggedItemType === "issue" && activeZone === zone
            && String(dragging.status).toLowerCase() !== String(column.title).toLowerCase();
          const wouldExceed = isTarget && limit && count + 1 > limit;
          const visibleCards = stage === "done" && !showAllDone ? cards.slice(0, DONE_PREVIEW) : cards;

          return (
            <section
              key={column._id || column.title}
              className={`kb-col is-${stage} ${over ? "is-over" : ""} ${isTarget ? "is-target" : ""} ${wouldExceed ? "is-target-over" : ""}`}
              aria-label={`${column.title}, ${count} ${count === 1 ? "card" : "cards"}`}
              {...getZoneProps(zone)}
              onDrop={() => {
                clearZone();
                onDropToColumn(column.title);
              }}
            >
              <header className="kb-col__head">
                <span className="kb-col__icon"><StageIcon /></span>
                <div className="kb-col__titles">
                  <h3 className="kb-col__title">{column.title}</h3>
                  <span className="kb-col__hint">{STAGE_META[stage].hint}</span>
                </div>
                {stage === "flow" ? (
                  <WipLimitControl
                    title={column.title}
                    count={count}
                    limit={limit}
                    canEdit={canEdit}
                    onSave={(value) => onWipSave(column._id, value)}
                  />
                ) : (
                  <span className="each-tasks-cls kb-col__count">{count}</span>
                )}
              </header>

              {stage === "flow" && limit ? (
                <div className="kb-wip" title={`${count} of ${limit} allowed`}>
                  <span style={{ width: `${Math.min(100, (count / limit) * 100)}%` }} />
                </div>
              ) : null}
              {over ? <p className="kb-col__warn">Over the WIP limit — finish something before pulling more.</p> : null}
              {wouldExceed ? <p className="kb-col__warn">Dropping here goes over the WIP limit of {limit}.</p> : null}

              <div className="kb-col__cards">
                {visibleCards.map((issue) => (
                  <KanbanCard
                    key={issue._id}
                    issue={issue}
                    stage={stage}
                    onOpen={onOpen}
                    assigning={assigneeUpdating === issue._id}
                    onAssign={(event, target) => setAssignFor({ anchor: event.currentTarget, issue: target })}
                    onDragStart={() => {
                      if (dragRef) dragRef.current = { id: issue._id, status: issue.status, draggedItemType: "issue" };
                    }}
                    onDragEnd={clearZone}
                  />
                ))}

                {!cards.length ? (
                  <div className="kb-col__empty">{stage === "queue" ? "Nothing waiting — add the next piece of work." : "Drop cards here"}</div>
                ) : null}

                {stage === "done" && cards.length > DONE_PREVIEW ? (
                  <button type="button" className="kb-more" onClick={() => setShowAllDone((v) => !v)}>
                    {showAllDone ? "Show fewer" : `Show all ${cards.length}`}
                  </button>
                ) : null}

                {stage === "queue" ? <AddCard onCreate={(summary) => onCreate(column.title, summary)} /> : null}
              </div>
            </section>
          );
        })}
      </div>

      <Popover
        open={Boolean(assignFor)}
        anchorEl={assignFor?.anchor}
        onClose={() => setAssignFor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "kb-assign-pop" } }}
      >
        {assignFor ? (
          <SearchableDropdown
            workFlowModalTaskData={assignFor.issue}
            members={contributors}
            handleAssigneeUpdate={async (id, user) => {
              await onAssigneeUpdate(id, user);
              setAssignFor(null);
            }}
          />
        ) : null}
      </Popover>
    </div>
  );
};

export default KanbanBoard;
